
/**
 * Communication Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useCommunicationStore } from '../../store/communication.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
export class CommunicationIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch communications and update store
     */
    async fetchCommunications(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useCommunicationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setCommunications(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create communication
     */
    async createCommunication(data) {
        const store = useCommunicationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('iso27001Communication:messages.createSuccess', { defaultValue: 'Communication created' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update communication
     */
    async updateCommunication(id, data) {
        const store = useCommunicationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('iso27001Communication:messages.updateSuccess', { defaultValue: 'Communication updated' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete communication
     */
    async deleteCommunication(id) {
        const store = useCommunicationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.delete(id);

            toast.success(i18next.t('iso27001Communication:messages.deleteSuccess', { defaultValue: 'Communication deleted' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete communications
     * @param {Array<string>} ids
     */
    async batchDeleteCommunication(ids) {
        const store = useCommunicationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('iso27001Communication:messages.bulkDeleteSuccess', { count: ids.length, defaultValue: `${ids.length} items deleted` }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Communication not found',
        });
    }
}
